import React, { ClipboardEventHandler, KeyboardEventHandler, useEffect, useState } from 'react';
import classNames from 'classnames';

import { TextInput } from 'src/common/form/text-input';
import { checkInputForUrl } from 'src/common/helpers/check-input-for-url';
import { IconButton } from 'src/common/icon-button';
import { useDelayedUnmount } from 'src/common/spotlight/use-delayed-unmount.hook';
import { RightStrongIcon } from 'src/monet/icons';
import LoadingSpinner from 'src/common/components/loading-spinner';

interface Props {
  isLoading: boolean;
  onUrlProcessed: (url: string) => void;
  personalizationDomain?: string;
}

export const PersonalizationDomainInput = ({ isLoading, onUrlProcessed, personalizationDomain }: Props) => {
  const [inputValue, setInputValue] = useState<string>('');

  const isValidUrl = Boolean(checkInputForUrl(inputValue));
  const showEnterButton = Boolean(isValidUrl);
  const { mounted: mountedEnterButton } = useDelayedUnmount(showEnterButton);

  useEffect(() => {
    setInputValue(personalizationDomain || '');
  }, [personalizationDomain]);

  const processInputValue = (value: string = inputValue) => {
    const url = checkInputForUrl(value);

    if (url && value !== personalizationDomain) {
      onUrlProcessed(url);
    }
  };

  const onProcessButtonClicked = () => {
    processInputValue();
  };

  const onKeyDowned: KeyboardEventHandler = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      processInputValue();
    }
  };

  const onInputPasted: ClipboardEventHandler = (event) => {
    const text = event.clipboardData.getData('text');
    processInputValue(text);
  };

  const onInputChanged: KeyboardEventHandler = (event) => {
    const { value } = event.target as HTMLInputElement;
    setInputValue(value);
  };

  return (
    <TextInput
      id='personalization_domain'
      trailingAddon={
        <div
          className={classNames('transition-opacity opacity-0', {
            invisible: !mountedEnterButton,
            visible: mountedEnterButton,
            'opacity-100': showEnterButton,
          })}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <IconButton onClick={onProcessButtonClicked} onKeyDown={onKeyDowned}>
              <RightStrongIcon className='text-bedrock-gray-medium group-hover:text-bedrock-brand-secondary group-focus:text-bedrock-brand-secondary group-focus-visible:text-bedrock-brand-secondary group-focus-within:text-bedrock-brand-secondary' />
            </IconButton>
          )}
        </div>
      }
      onChange={onInputChanged}
      onKeyDown={onKeyDowned}
      onBlur={(e: any) => processInputValue(e.target.value)}
      inputProps={{
        type: 'text',
        name: 'personalization_domain',
        onPaste: onInputPasted,
        value: inputValue,
        placeholder: 'https://acme.com',
      }}
    />
  );
};
